import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { api, eur, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { ArrowLeft, Check } from "lucide-react";

export function CheckoutPage() {
  const { user } = useAuth();
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    full_name: "", street: "", city: "", zip: "", province: "", phone: "",
  });

  useEffect(() => {
    if (user === false) nav("/accedi?redirect=/checkout");
    if (user && user.id && !form.full_name) setForm((f) => ({ ...f, full_name: user.name || "" }));
  }, [user, nav, form.full_name]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-[#5C5A56]">Il tuo carrello è vuoto.</p>
        <Link to="/shop" className="btn-primary mt-6">Vai alla boutique</Link>
      </div>
    );
  }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const shipping = subtotal >= 50 ? 0 : 6.9;
  const total = subtotal + shipping;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      const address = { ...form, country: "IT" };
      const { data } = await api.post("/orders", {
        order_type: "product",
        items: items.map((i) => ({ item_type: "product", item_id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        shipping_address: address,
      });
      const { data: co } = await api.post("/payments/checkout", { order_id: data.id, origin_url: window.location.origin });
      clear();
      window.location.href = co.url;
    } catch (ex) {
      setErr(formatError(ex));
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      <Link to="/carrello" className="text-sm text-[#5C5A56] hover:text-[#1C1B1A] inline-flex items-center gap-1 mb-8"><ArrowLeft size={14} /> Torna al carrello</Link>
      <h1 className="font-serif text-5xl leading-tight">Checkout</h1>

      <form onSubmit={submit} className="mt-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="overline mb-4">Indirizzo di spedizione</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input className="input-field" required placeholder="Nome e cognome" value={form.full_name} onChange={update("full_name")} data-testid="checkout-name" />
            <input className="input-field" required placeholder="Telefono" value={form.phone} onChange={update("phone")} data-testid="checkout-phone" />
            <input className="input-field sm:col-span-2" required placeholder="Via e numero civico" value={form.street} onChange={update("street")} data-testid="checkout-street" />
            <input className="input-field" required placeholder="Città" value={form.city} onChange={update("city")} data-testid="checkout-city" />
            <div className="grid grid-cols-2 gap-4">
              <input className="input-field" required placeholder="CAP" value={form.zip} onChange={update("zip")} data-testid="checkout-zip" />
              <input className="input-field" required placeholder="Provincia" value={form.province} onChange={update("province")} maxLength={2} data-testid="checkout-province" />
            </div>
          </div>

          {err && <div className="mt-6 p-4 bg-[#9E413B]/10 text-[#9E413B] text-sm border border-[#9E413B]/30" data-testid="checkout-error">{err}</div>}
        </div>

        <div>
          <div className="card-luxury p-6 sticky top-24">
            <div className="overline mb-4">Riepilogo</div>
            <div className="space-y-2 text-sm max-h-56 overflow-y-auto">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between gap-2">
                  <span className="truncate">{i.name} × {i.quantity}</span>
                  <span>{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="hairline my-4" />
            <div className="flex justify-between text-sm py-1"><span>Subtotale</span><span>{eur(subtotal)}</span></div>
            <div className="flex justify-between text-sm py-1"><span>Spedizione</span><span>{shipping === 0 ? "Gratis" : eur(shipping)}</span></div>
            <div className="flex justify-between text-lg font-serif pt-3 mt-3 border-t border-[#E4DFD5]">
              <span>Totale</span>
              <span className="text-[#B97A48]">{eur(total)}</span>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center mt-6" data-testid="pay-btn">
              {submitting ? "Pagamento in corso…" : "Procedi al pagamento"}
            </button>
            <div className="text-xs text-[#5C5A56] mt-3 text-center">Pagamento sicuro · Spedizione SDA o DHL</div>
          </div>
        </div>
      </form>
    </div>
  );
}

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      if (n > 7) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setStatus("error");
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success">
      {status === "checking" && (
        <>
          <div className="w-16 h-16 mx-auto rounded-full border-2 border-[#B97A48] border-t-transparent animate-spin" />
          <h1 className="font-serif text-3xl mt-8">Verifica del pagamento…</h1>
          <p className="text-[#5C5A56] mt-3 text-sm">Tentativo {attempts + 1} di 8</p>
        </>
      )}
      {status === "paid" && (
        <>
          <div className="w-16 h-16 mx-auto rounded-full bg-[#4A6B53] text-white flex items-center justify-center">
            <Check size={28} strokeWidth={2.5} />
          </div>
          <h1 className="font-serif text-4xl mt-8">Pagamento confermato</h1>
          <p className="text-[#5C5A56] mt-4">Grazie per aver scelto Premium Shoes Care. Entro poche ore riceverai via email l'etichetta di spedizione prepagata (SDA o DHL) con le istruzioni.</p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/ordini" className="btn-primary" data-testid="go-to-orders">I miei ordini</Link>
            {orderId && <Link to={`/ordini/${orderId}`} className="btn-secondary">Dettaglio ordine</Link>}
          </div>
        </>
      )}
      {(status === "timeout" || status === "expired" || status === "error") && (
        <>
          <h1 className="font-serif text-4xl">Qualcosa è andato storto</h1>
          <p className="text-[#5C5A56] mt-4">
            {status === "timeout" && "Non siamo riusciti a confermare il pagamento. Controlla 'I miei ordini' tra qualche minuto."}
            {status === "expired" && "La sessione di pagamento è scaduta."}
            {status === "error" && "Errore durante la verifica."}
          </p>
          <Link to="/ordini" className="btn-primary mt-6">Vai ai miei ordini</Link>
        </>
      )}
    </div>
  );
}

export function CheckoutCancel() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-cancel">
      <h1 className="font-serif text-4xl">Pagamento annullato</h1>
      <p className="text-[#5C5A56] mt-4">Non è stato addebitato alcun importo.</p>
      <div className="mt-8 flex gap-3 justify-center">
        <Link to="/ordini" className="btn-secondary">I miei ordini</Link>
        {orderId && <Link to={`/ordini/${orderId}`} className="btn-primary">Riprova pagamento</Link>}
      </div>
    </div>
  );
}
